import React, { useEffect, useState, useCallback } from 'react';
import { TrendingUp, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { goldRateAPI } from '../../api/goldRate';
import PageHeader from '../../components/ui/PageHeader';
import Input      from '../../components/ui/Input';
import Button     from '../../components/ui/Button';

// ── Helpers ─────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);

const formatRate = (val) =>
  val ? `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// ── Component ───────────────────────────────────────────────
export default function GoldRatePage() {
  // current rate state
  const [current, setCurrent]       = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // form state
  const [form, setForm]     = useState({ rate: '', date: today() });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]   = useState(null); // { type: 'success'|'error', message }

  // ── Fetch current gold rate ────────────────────────────────
  const loadCurrent = useCallback(async () => {
    setFetchLoading(true);
    setFetchError('');
    try {
      const { data } = await goldRateAPI.getCurrent();
      if (data.status) {
        setCurrent(data.data);
        // Pre-fill form with current values so admin can tweak easily
        setForm({ rate: data.data.rate, date: data.data.date });
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        // No rate set yet — that's fine, leave form empty
        setCurrent(null);
      } else {
        setFetchError(
          err?.response?.data?.message || 'Failed to load current gold rate.'
        );
      }
    } finally {
      setFetchLoading(false);
    }
  }, []);

  useEffect(() => { loadCurrent(); }, [loadCurrent]);

  // ── Auto-dismiss toast ────────────────────────────────────
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Form handlers ─────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.rate || isNaN(Number(form.rate)) || Number(form.rate) <= 0)
      errs.rate = 'Enter a valid rate greater than 0';
    if (!form.date)
      errs.date = 'Date is required';
    return errs;
  };

  // ── Submit — POST /api/admin/gold-rate ────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const { data } = await goldRateAPI.save({
        rate: parseFloat(form.rate),
        date: form.date,
      });

      if (data.status) {
        setCurrent(data.data);
        setToast({ type: 'success', message: data.message || 'Gold rate updated successfully' });
      } else {
        setToast({ type: 'error', message: data.message || 'Update failed.' });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.rate?.[0] ||
        err?.response?.data?.errors?.date?.[0] ||
        'Something went wrong. Please try again.';
      setToast({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader title="Gold Rate" />

      <div className="flex flex-col gap-4 p-5">

        {/* ── Current rate display card ── */}
        <div className="card-gold rounded-2xl p-5 animate-fade-up">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest"
                 style={{ color: 'rgba(255,255,255,0.7)' }}>
                Current Rate / gram
              </p>

              {fetchLoading ? (
                <div className="skeleton mt-2 h-9 w-40 rounded-xl" />
              ) : fetchError ? (
                <p className="mt-2 text-sm text-white/70">{fetchError}</p>
              ) : current ? (
                <>
                  <p className="mt-1 text-3xl font-bold font-serif text-white">
                    {formatRate(current.rate)}
                  </p>
                  <p className="mt-0.5 text-xs text-white/70">
                    as of {formatDate(current.date)}
                  </p>
                </>
              ) : (
                <p className="mt-2 text-sm text-white/70">No rate set yet</p>
              )}
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full"
                   style={{ background: 'rgba(255,255,255,0.2)' }}>
                <TrendingUp size={18} className="text-white" />
              </div>
              <button
                onClick={loadCurrent}
                disabled={fetchLoading}
                className="flex h-7 w-7 items-center justify-center rounded-xl transition-all active:scale-90 disabled:opacity-50"
                style={{ background: 'rgba(255,255,255,0.15)' }}
                aria-label="Refresh"
              >
                <RefreshCw size={13} className={`text-white ${fetchLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Update form card ── */}
        <div className="card animate-fade-up" style={{ animationDelay: '80ms' }}>
          <h2 className="font-serif text-base font-semibold text-white mb-0.5">
            Update Gold Rate
          </h2>
          <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
            If a rate already exists for the selected date it will be overwritten.
          </p>

          {/* Toast */}
          {toast && (
            <div
              className="mb-4 flex items-start gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
              style={{
                background: toast.type === 'success'
                  ? 'rgba(5,150,105,0.15)' : 'rgba(239,68,68,0.12)',
                border: `1px solid ${toast.type === 'success'
                  ? 'rgba(5,150,105,0.35)' : 'rgba(239,68,68,0.3)'}`,
                color: toast.type === 'success' ? '#6ee7b7' : '#fca5a5',
              }}
            >
              {toast.type === 'success'
                ? <CheckCircle2 size={15} className="mt-0.5 flex-shrink-0" />
                : <AlertCircle  size={15} className="mt-0.5 flex-shrink-0" />}
              <span>{toast.message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">

            {/* Rate */}
            <Input
              label="Rate (₹ per gram)"
              type="number"
              name="rate"
              placeholder="e.g. 6850.00"
              value={form.rate}
              onChange={handleChange}
              error={errors.rate}
              min="0"
              step="0.01"
            />

            {/* Date */}
            <Input
              label="Date"
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              error={errors.date}
            />

            <Button
              type="submit"
              variant="gold"
              fullWidth
              loading={submitting}
            >
              Save Gold Rate
            </Button>

          </form>
        </div>

        {/* Info note */}
        <p className="px-1 text-xs text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>
          Gold rate is displayed on user dashboards for reference only — it does not affect investment calculations.
        </p>

      </div>
    </div>
  );
}
